import React, { useEffect, Suspense } from 'react';
import Lenis from 'lenis';
import { Canvas } from '@react-three/fiber';
import { Loader } from '@react-three/drei';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';

gsap.registerPlugin(ScrollTrigger);

// Home section components
import ColliderExperience from '../components/home/ColliderExperience';
import TheInjector from '../components/home/TheInjector';
import TheCollision from '../components/home/TheCollision';
import SimulatorShowcase from '../components/home/SimulatorShowcase';
import TheExperiment from '../components/home/TheExperiment';
import TheBeamline from '../components/home/TheBeamline';
import TheCalorimeter from '../components/home/TheCalorimeter';
import TheLearnCTA from '../components/home/TheLearnCTA';
import TheProposal from '../components/home/TheProposal';
import TeamPreview from '../components/home/TeamPreview';
import TheDecayChamber from '../components/home/TheDecayChamber';
import Footer from '../components/common/Footer';

const Home = () => {

    // Initialize Lenis Smooth Scroll
    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.6, // Increased as requested
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            direction: 'vertical',
            gestureDirection: 'vertical',
            smooth: true,
            mouseMultiplier: 1,
            smoothTouch: false,
            touchMultiplier: 2,
        });

        // Optimization
        gsap.ticker.lagSmoothing(0);
        ScrollTrigger.normalizeScroll(true);

        // Connect Lenis to GSAP ScrollTrigger
        lenis.on('scroll', ScrollTrigger.update);

        function raf(time) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }

        requestAnimationFrame(raf);

        return () => {
            ScrollTrigger.getAll().forEach(t => t.kill(true));
            lenis.destroy();
        };
    }, []);

    return (
        <div className="w-full bg-[#030303] text-white selection:bg-blue-500 selection:text-white">

            {/* 1. FIXED WEBGL LAYER */}

            {/* NOISE OVERLAY (Cinematic Feel) */}
            <div className="fixed inset-0 pointer-events-none z-[5] opacity-[0.03] mix-blend-overlay"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.5'/%3E%3C/svg%3E")` }}>
            </div>

            <div className="fixed inset-0 z-0 h-screen w-full bg-black pointer-events-none">
                <Canvas
                    key="home-canvas"
                    gl={{ antialias: false, powerPreference: "high-performance" }}
                    dpr={[1, 1.5]}
                    camera={{ position: [0, 0, 10], fov: 40 }}
                >
                    <Suspense fallback={null}>
                        <ColliderExperience />
                    </Suspense>
                </Canvas>
            </div>

            {/* 2. SCROLL CONTENT LAYER */}
            <main data-cursor-invert className="relative z-10 w-full overflow-x-hidden">
                <TheInjector />
                <div className="home-continuum">
                    <TheCollision />
                    <SimulatorShowcase />
                    <TheExperiment />
                    <TheBeamline />
                    <TheCalorimeter />
                    <TheLearnCTA />
                    <TheProposal />
                    <TeamPreview />
                    <TheDecayChamber />
                    <Footer />
                </div>
            </main>

            {/* Global Loader */}
            <Loader
                containerStyles={{ background: '#000' }}
                barStyles={{ background: '#3b82f6' }}
                dataStyles={{ fontFamily: 'monospace', fontSize: '10px' }}
            />
        </div>
    );
};

export default Home;
